import React from 'react';

const ProfessionalConduct = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-indigo-700 px-8 py-6 text-center">
          <img
            src="https://scholars-uploads.s3.us-east-2.amazonaws.com/headshots/nacme_logo.jpg"
            alt="NACME"
            className="h-10 mx-auto mb-3 rounded"
          />
          <h1 className="text-2xl font-bold text-white">Standards of Professional Conduct</h1>
          <p className="text-indigo-200 text-sm mt-1">NACME Executive Relationship Program</p>
        </div>

        <div className="px-8 py-8 space-y-8">
          {/* Section 1 */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">1. Purpose</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              These standards govern interactions facilitated through the NACME Executive Relationship
              Program. All participants &mdash; both requesters and respondents &mdash; agree to conduct
              themselves professionally and in good faith.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">2. Expected Conduct</h2>
            <p className="text-sm text-gray-700 mb-3">Participants shall:</p>
            <ul className="space-y-3 text-sm text-gray-700">
              <li className="flex items-start">
                <span className="text-indigo-500 mr-2 mt-0.5 flex-shrink-0">&bull;</span>
                <span>
                  <strong>Maintain a career-focused purpose.</strong> All interactions must relate to career
                  development, professional growth, mentorship, industry knowledge, or board service.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-indigo-500 mr-2 mt-0.5 flex-shrink-0">&bull;</span>
                <span>
                  <strong>Respect boundaries.</strong> Honor the other party's time, availability, and
                  preferred communication methods. If someone declines a request or sets limits, respect
                  their decision without pressure.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-indigo-500 mr-2 mt-0.5 flex-shrink-0">&bull;</span>
                <span>
                  <strong>Communicate honestly.</strong> Represent your background, intentions, and
                  qualifications truthfully.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-indigo-500 mr-2 mt-0.5 flex-shrink-0">&bull;</span>
                <span>
                  <strong>Preserve confidentiality.</strong> Information shared during facilitated
                  introductions is confidential. Do not share the other party's contact information,
                  career details, or conversation content with third parties without explicit permission.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-indigo-500 mr-2 mt-0.5 flex-shrink-0">&bull;</span>
                <span>
                  <strong>Respond in a timely manner.</strong> Make a good-faith effort to respond to
                  scheduled meetings and communications within a reasonable timeframe.
                </span>
              </li>
            </ul>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">3. Prohibited Conduct</h2>
            <p className="text-sm text-gray-700 mb-3">Participants shall not:</p>
            <ul className="space-y-3 text-sm text-gray-700">
              <li className="flex items-start">
                <span className="text-red-400 mr-2 mt-0.5 flex-shrink-0">&bull;</span>
                <span>
                  Use facilitated introductions for <strong>sales, solicitation, recruiting for their own
                  company, or commercial purposes</strong> unrelated to the stated career development goal.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-red-400 mr-2 mt-0.5 flex-shrink-0">&bull;</span>
                <span>
                  Engage in <strong>harassment, discrimination, or intimidation</strong> of any kind,
                  including but not limited to conduct based on race, gender, age, religion, sexual
                  orientation, disability, or national origin.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-red-400 mr-2 mt-0.5 flex-shrink-0">&bull;</span>
                <span>
                  Make <strong>unwelcome personal or romantic advances</strong>.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-red-400 mr-2 mt-0.5 flex-shrink-0">&bull;</span>
                <span>
                  Misrepresent their <strong>identity, credentials, or intentions</strong>.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-red-400 mr-2 mt-0.5 flex-shrink-0">&bull;</span>
                <span>
                  Attempt to <strong>circumvent the introduction process</strong> by contacting the other
                  party through channels not facilitated by NACME before consent is granted.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-red-400 mr-2 mt-0.5 flex-shrink-0">&bull;</span>
                <span>
                  Share or use information obtained through facilitated introductions for{' '}
                  <strong>competitive intelligence or unauthorized purposes</strong>.
                </span>
              </li>
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">4. Consequences of Violations</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              NACME reserves the right to:
            </p>
            <ul className="space-y-2 text-sm text-gray-700 mt-3">
              <li className="flex items-start">
                <span className="text-gray-400 mr-2 mt-0.5 flex-shrink-0">&bull;</span>
                <span>
                  Immediately suspend or permanently remove any participant who violates these standards.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-gray-400 mr-2 mt-0.5 flex-shrink-0">&bull;</span>
                <span>
                  Revoke access to the NACME Executive Relationship Program and related NACME programs.
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-gray-400 mr-2 mt-0.5 flex-shrink-0">&bull;</span>
                <span>
                  Report conduct that may constitute harassment or illegal activity to the appropriate
                  authorities.
                </span>
              </li>
            </ul>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">5. Reporting Concerns</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              If you experience or witness conduct that violates these standards, please contact NACME at{' '}
              <a
                href="mailto:contributions@nacme.org"
                className="text-indigo-600 underline hover:text-indigo-800"
              >
                contributions@nacme.org
              </a>
              . All reports will be reviewed confidentially. NACME prohibits retaliation against anyone who
              reports a concern in good faith.
            </p>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">6. Acknowledgment</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              By participating in the NACME Executive Relationship Program &mdash; whether as a requester
              or respondent &mdash; you acknowledge that you have read, understood, and agree to abide by
              these standards of professional conduct.
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-8 py-4 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">
            NACME Executive Relationship Program
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalConduct;
